/**
 * QRCodesTab - QR code download cards for each campaign (FULLY DYNAMIC)
 */
import { Download, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/components/admin/shared/constants';
import { getCampaignTheme, LEGACY_CAMPAIGN_NAMES, PRODUCTION_DOMAIN } from './campaignUtils';

export default function QRCodesTab({ 
  report, 
  allCampaigns,
  qrCodes, 
  onDownloadQR 
}) {
  // Build dynamic campaign list from allCampaigns OR report.campaigns
  const campaignList = allCampaigns?.length > 0 
    ? allCampaigns 
    : report?.campaigns?.map(c => ({ campaign_id: c.campaign_id, title: c.title, theme: c.theme })) || [];

  return (
    <div className="space-y-8">
      {/* Important Notice */}
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <ExternalLink className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-medium text-green-800 dark:text-green-200 text-lg">QR Codes Point to bulaccinocafe.com</h3>
            <p className="text-green-700 dark:text-green-300 text-sm mt-1">
              When customers scan these QR codes, they will be directed to <strong>bulaccinocafe.com/join/[campaign_id]</strong> where they can sign up.
              Their information will automatically populate in this admin dashboard.
            </p>
          </div>
        </div>
      </div>

      {/* QR Code Download Cards - DYNAMIC */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {campaignList.map(campaignData => {
          const id = campaignData.campaign_id;
          const theme = getCampaignTheme(id, campaignData.theme);
          const qrData = qrCodes[id];
          const reportCampaign = report?.campaigns?.find(c => c.campaign_id === id);
          const campaignName = campaignData.title || LEGACY_CAMPAIGN_NAMES[id] || `Campaign ${id}`;
          
          return (
            <div 
              key={id}
              className={`bg-card rounded-2xl border-2 ${theme.border} overflow-hidden`}
              data-testid={`qr-card-campaign-${id}`}
            >
              <div className={`bg-gradient-to-r ${theme.gradient} p-4 text-white`}>
                <p className="font-bold text-lg">Campaign {id}</p>
                <p className="text-sm opacity-90 truncate">{campaignName}</p>
              </div>

              <div className="p-6">
                {/* QR Code Preview */}
                <div className="bg-white dark:bg-gray-100 rounded-xl p-6 mb-4 flex justify-center shadow-inner">
                  {qrData?.base64 ? (
                    <img 
                      src={qrData.base64}
                      alt={`Campaign ${id} QR`}
                      className="w-48 h-48"
                    />
                  ) : (
                    <img 
                      src={`${API}/api/campaigns/${id}/qr?size=200`}
                      alt={`Campaign ${id} QR`}
                      className="w-48 h-48"
                    />
                  )}
                </div>

                {/* Target URL */}
                <div className="bg-muted rounded-lg p-3 mb-4">
                  <p className="text-xs text-muted-foreground mb-1">Scans to:</p>
                  <code className="text-sm font-mono text-primary break-all">
                    {PRODUCTION_DOMAIN}/join/{id}
                  </code>
                </div>

                {/* Download Options */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">Download for Print:</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="rounded-full"
                      onClick={() => onDownloadQR(id, 300)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      300px
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="rounded-full"
                      onClick={() => onDownloadQR(id, 500)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      500px
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="rounded-full"
                      onClick={() => onDownloadQR(id, 800)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      800px
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="rounded-full"
                      onClick={() => onDownloadQR(id, 1200)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      1200px
                    </Button>
                  </div>
                  <Button 
                    className={`w-full rounded-full mt-2 bg-gradient-to-r ${theme.gradient} text-white`}
                    onClick={() => onDownloadQR(id, 2000)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download High-Res (2000px)
                  </Button>
                </div>

                {/* Stats */}
                <div className="mt-4 pt-4 border-t border-border text-center">
                  <p className={`text-2xl font-bold ${theme.color}`}>{reportCampaign?.signups || 0}</p>
                  <p className="text-sm text-muted-foreground">Signups from this QR</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Print Instructions */}
      <div className="bg-card rounded-2xl p-6 border border-border">
        <h3 className="font-heading text-lg text-foreground mb-4">Printing Tips</h3>
        <div className="grid md:grid-cols-2 gap-6 text-sm text-muted-foreground">
          <div>
            <p className="font-medium text-foreground mb-2">For Posters & Banners:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Download the 2000px high-resolution version</li>
              <li>QR codes work best at minimum 2cm × 2cm printed size</li>
              <li>Ensure good contrast - dark QR on light background</li>
              <li>Test scan before mass printing</li>
            </ul>
          </div>
          <div>
            <p className="font-medium text-foreground mb-2">For Flyers & Small Print:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>500px or 800px versions work well</li>
              <li>Keep clear space around the QR code</li>
              <li>Don't resize smaller than 1.5cm × 1.5cm</li>
              <li>Avoid glossy surfaces that cause reflection</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
